<template>
    <b-modal 
      v-model="isModalOpen" 
      has-modal-card 
      trap-focus 
      :can-cancel="!processingPayment"
      aria-role="dialog" 
      aria-modal
      @close="onClose"
    >
      <div class="modal-card" style="width: auto">
        <header class="modal-card-head">
          <p class="modal-card-title">
            <b-icon icon="credit-card" size="is-small"></b-icon>
            Registrar Pago Manual
          </p>
        </header>
        
        <section class="modal-card-body">
          <div class="box" style="background-color: #fafafa; margin-bottom: 1.5rem;">
            <p class="subtitle is-6" style="margin-bottom: 0.5rem;">
              <strong>Member:</strong> {{ member.Nombre }} {{ member.ApellidoPaterno }}
            </p>
            <p class="subtitle is-6" style="margin-bottom: 0.5rem;">
              <strong>Email:</strong> {{ member.Email }}
            </p>
            <p class="subtitle is-6" style="margin-bottom: 0;">
              <strong>Contrato:</strong> {{ memberContract }}
            </p>
          </div>
  
          <form @submit.prevent="processManualPayment">
            <b-field 
              label="Payment Intent ID" 
              :type="paymentIntentError ? 'is-danger' : (paymentIntentInfo && paymentIntentInfo.success ? 'is-success' : '')" 
              :message="paymentIntentError"
            >
              <b-input 
                v-model="form.paymentIntentId"
                placeholder="pi_1234567890abcdef..."
                :disabled="processingPayment || loadingPaymentIntent"
                @input="onPaymentIntentChange"
                icon="link"
                :loading="loadingPaymentIntent"
              ></b-input>
            </b-field>
  
            <div v-if="paymentIntentInfo && paymentIntentInfo.success" class="box" style="background-color: #f0f8f0; margin-bottom: 1.5rem;">
              <p class="subtitle is-6 has-text-success" style="margin-bottom: 1rem;">
                <b-icon icon="check-circle" size="is-small"></b-icon>
                Payment Intent Encontrado
              </p>
              
              <div class="columns is-multiline">
                <div class="column is-half">
                  <p><strong>Monto:</strong> {{ formattedAmount }}</p>
                  <p><strong>Estado:</strong> 
                    <b-tag type="is-success" size="is-small">{{ paymentStatus }}</b-tag>
                  </p>
                  <p><strong>Cuenta:</strong> 
                    <b-tag :type="accountTagType" size="is-small">
                      Vacancy Rewards
                    </b-tag>
                  </p>
                </div>
                <div class="column is-half">
                  <p><strong>Invoice:</strong> {{ invoiceNumber || 'N/A' }}</p>
                  <p><strong>Contrato:</strong> {{ contractNumber || 'Del member actual' }}</p>
                  <p><strong>Total Mensualidades:</strong> {{ totalMensualidades || 'Requiere captura manual' }}</p>
                </div>
                <div class="column is-full">
                  <p style="font-size: 0.9rem; color: #666; margin-top: 0.5rem;">
                    <strong>Descripción:</strong> {{ paymentDescription }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Advertencia si no hay estructura válida -->
            <div v-if="paymentIntentInfo && paymentIntentInfo.success && !hasValidStructure" class="box" style="background-color: #fff3cd; border-left: 4px solid #ffc107; margin-bottom: 1.5rem;">
              <p class="subtitle is-6" style="margin-bottom: 0.5rem; color: #856404;">
                <b-icon icon="alert-triangle" size="is-small"></b-icon>
                <strong>Estructura No Válida Detectada</strong>
              </p>
              <p style="font-size: 0.9rem; color: #856404;">
                Este Payment Intent no contiene la estructura estándar de Casa Linda en su descripción. 
                Será necesario capturar manualmente el <strong>Total de Mensualidades</strong>.
                <br><br>
                <strong>¿Desea continuar con la captura manual?</strong>
              </p>
            </div>
  
            <!-- Total de Mensualidades (solo para casos sin estructura válida) -->
            <b-field 
              v-if="paymentIntentInfo && paymentIntentInfo.success && !hasValidStructure"
              label="Total de Mensualidades" 
              :type="totalMensualidadesError ? 'is-danger' : ''" 
              :message="totalMensualidadesError"
            >
              <b-numberinput 
                v-model="form.totalMensualidades"
                :min="1"
                :max="99"
                :disabled="processingPayment"
                placeholder="Ej: 24"
                controls-position="compact"
              ></b-numberinput>
            </b-field>

            <b-field 
              label="Número de Mensualidad" 
              :type="mensualidadError ? 'is-danger' : ''" 
              :message="mensualidadError"
            >
              <b-numberinput 
                v-model="form.mensualidad"
                :min="1"
                :max="maxMensualidades"
                :disabled="processingPayment || !(paymentIntentInfo && paymentIntentInfo.success)"
                placeholder="Ej: 5"
                controls-position="compact"
              ></b-numberinput>
            </b-field>
  
            <div v-if="paymentIntentInfo && paymentIntentInfo.success" class="columns is-vcentered">
              <div class="column is-three-quarters">
                <b-field label="Mensualidad sugerida (del invoice)">
                  <b-input 
                    :value="suggestedMensualidad"
                    readonly
                    type="is-info"
                  ></b-input>
                </b-field>
              </div>
              <div class="column is-one-quarter">
                <div style="margin-top: 1.5rem;">
                  <b-button 
                    type="is-info" 
                    outlined 
                    size="is-small"
                    @click="useSuggestedMensualidad"
                    :disabled="processingPayment"
                    expanded
                  >
                    Usar Sugerida
                  </b-button>
                </div>
              </div>
            </div>
  
            <div v-if="paymentIntentInfo && paymentIntentInfo.success && form.mensualidad" class="box" style="background-color: #fff8e1; margin-top: 1rem;">
              <p class="subtitle is-6" style="margin-bottom: 0.5rem;">
                <strong>Confirmación:</strong>
              </p>
              <p style="font-size: 0.9rem;">
                Se registrará el pago de la <strong>mensualidad {{ form.mensualidad }}</strong>
                <span v-if="!hasValidStructure">de <strong>{{ form.totalMensualidades || '?' }} total</strong></span>
                por un monto de <strong>{{ formattedAmount }}</strong>
                para el contrato <strong>{{ contractNumber || memberContract }}</strong>.
              </p>
              <p v-if="!hasValidStructure" style="font-size: 0.8rem; color: #856404; margin-top: 0.5rem;">
                <b-icon icon="alert-triangle" size="is-small"></b-icon>
                <em>Captura manual - Sin estructura estándar en el Payment Intent</em>
              </p>
            </div>
          </form>
        </section>
  
        <footer class="modal-card-foot">
          <b-button 
            type="is-primary" 
            :loading="processingPayment"
            :disabled="!canSubmit"
            @click="processManualPayment"
          >
            Registrar Pago
          </b-button>
          <b-button 
            type="is-light" 
            @click="closeModal"
            :disabled="processingPayment"
          >
            Cancelar
          </b-button>
        </footer>
      </div>
    </b-modal>
  </template>
  
  <script>
  import axios from 'axios'
  const API_URL = process.env.VUE_APP_API_URL;
  export default {
    name: 'ManualPaymentModal',
    props: {
      value: {
        type: Boolean,
        default: false
      },
      member: {
        type: Object,
        required: true,
        default: () => ({})
      }
    },
    data() {
      return {
        form: {
          paymentIntentId: '',
          mensualidad: null,
          totalMensualidades: null
        },
        
        processingPayment: false,
        loadingPaymentIntent: false,
        
        paymentIntentInfo: null,
        
              paymentIntentError: '',
      mensualidadError: '',
      totalMensualidadesError: '',
        
        paymentIntentTimer: null
      }
    },
    computed: {
      isModalOpen: {
        get() {
          return this.value
        },
        set(value) {
          this.$emit('input', value)
        }
      },
      
          canSubmit() {
      const basicValidation = this.form.paymentIntentId.trim() !== '' &&
                             this.form.mensualidad &&
                             this.form.mensualidad > 0 &&
                             (this.paymentIntentInfo && this.paymentIntentInfo.success) &&
                             !this.processingPayment &&
                             !this.paymentIntentError &&
                             !this.mensualidadError &&
                             !this.totalMensualidadesError &&
                             !this.loadingPaymentIntent
      
      if (!this.hasValidStructure && this.paymentIntentInfo && this.paymentIntentInfo.success) {
        return basicValidation && this.form.totalMensualidades && this.form.totalMensualidades > 0
      }
      
      return basicValidation
    },
      
      memberContract() {
        return this.member.NumeroContrato || this.member.ContratoId || 'No disponible'
      },
      
      formattedAmount() {
        const amount = this.safeGet(this.paymentIntentInfo, 'data.paymentIntent.amount')
        if (!amount) return ''
        const currency = this.safeGet(this.paymentIntentInfo, 'data.paymentIntent.currency', 'USD')
        return `$${(amount / 100).toFixed(2)} ${currency.toUpperCase()}`
      },
      
      paymentStatus() {
        return this.safeGet(this.paymentIntentInfo, 'data.paymentIntent.status')
      },
      
      paymentAccount() {
        const account = this.safeGet(this.paymentIntentInfo, 'data.paymentIntent.account')
        if (account === 'renewals') return 'Vacancy Rewards'
        if (account) return 'Astuto Travel'
        return ''
      },
      
      accountTagType() {
        return this.paymentAccount === 'renewals' ? 'is-info' : 'is-warning'
      },
      
      invoiceNumber() {
        return this.safeGet(this.paymentIntentInfo, 'data.invoice.number')
      },
      
      contractNumber() {
        return this.safeGet(this.paymentIntentInfo, 'data.contract.numero')
      },
      
      totalMensualidades() {
        return this.safeGet(this.paymentIntentInfo, 'data.invoice.totalMensualidades')
      },
      
      paymentDescription() {
        return this.safeGet(this.paymentIntentInfo, 'data.paymentIntent.description')
      },
      
      suggestedMensualidad() {
        return this.safeGet(this.paymentIntentInfo, 'data.invoice.mensualidad')
      },
      
          maxMensualidades() {
      // Si hay estructura válida, usar el total del PI, sino usar el valor manual, sino 99 por defecto
      return this.safeGet(this.paymentIntentInfo, 'data.invoice.totalMensualidades') || 
             this.form.totalMensualidades || 
             99
    },
    
    hasValidStructure() {
      return this.safeGet(this.paymentIntentInfo, 'data.hasValidStructure', false)
    }
    },
    watch: {
      value(newVal) {
        if (newVal) {
          this.resetForm()
        }
      },
      
          'form.mensualidad'(newVal) {
      this.mensualidadError = ''
      if (newVal && this.paymentIntentInfo && this.paymentIntentInfo.success) {
        if (newVal < 1 || newVal > this.maxMensualidades) {
          this.mensualidadError = `La mensualidad debe estar entre 1 y ${this.maxMensualidades}`
        }
      }
    },
    
    'form.totalMensualidades'(newVal) {
      this.totalMensualidadesError = ''
      if (newVal && newVal < 1) {
        this.totalMensualidadesError = 'El total de mensualidades debe ser mayor a 0'
      }
      if (newVal && this.form.mensualidad && newVal < this.form.mensualidad) {
        this.totalMensualidadesError = 'El total de mensualidades no puede ser menor que la mensualidad actual'
      }
    }
    },
    methods: {
      // Helper para validar objetos anidados sin optional chaining
      safeGet(obj, path, defaultValue = '') {
        return path.split('.').reduce((current, key) => {
          return current && current[key] !== undefined ? current[key] : null
        }, obj) || defaultValue
      },
      
      resetForm() {
        this.form = {
          paymentIntentId: '',
          mensualidad: null,
          totalMensualidades: null
        }
        this.paymentIntentInfo = null
        this.paymentIntentError = ''
        this.mensualidadError = ''
        this.totalMensualidadesError = ''
        this.processingPayment = false
        this.loadingPaymentIntent = false
        
        if (this.paymentIntentTimer) {
          clearTimeout(this.paymentIntentTimer)
          this.paymentIntentTimer = null
        }
      },
      
      closeModal() {
        this.isModalOpen = false
      },
      
      onClose() {
        this.resetForm()
        this.$emit('close')
      },
      
      onPaymentIntentChange() {
        this.paymentIntentError = ''
        this.paymentIntentInfo = null
        this.form.mensualidad = null
        this.form.totalMensualidades = null
        this.mensualidadError = ''
        this.totalMensualidadesError = ''
        
        if (this.paymentIntentTimer) {
          clearTimeout(this.paymentIntentTimer)
        }
        
        if (this.form.paymentIntentId.trim()) {
          this.paymentIntentTimer = setTimeout(() => {
            this.fetchPaymentIntentInfo()
          }, 1000)
        }
      },
      
      useSuggestedMensualidad() {
        if (this.suggestedMensualidad) {
          this.form.mensualidad = this.suggestedMensualidad
        }
      },
      
      async fetchPaymentIntentInfo() {
        if (!this.form.paymentIntentId.trim()) return
        
        this.loadingPaymentIntent = true
        this.paymentIntentError = ''
        
        try {
          const response = await axios.get(`${API_URL}/financing/payment-intent/${this.form.paymentIntentId}`)

          if (response.data.success) {
            this.paymentIntentInfo = response.data
            this.$nextTick(() => {
              if (this.suggestedMensualidad) {
                this.form.mensualidad = this.suggestedMensualidad
              }
            })

            // Solo validar contrato si hay estructura válida
            if (response.data.data.hasValidStructure && response.data.data.contract && response.data.data.contract.numero !== (this.member.NumeroContrato || this.member.ContratoId)) {
              this.paymentIntentError = `El contrato del Payment Intent (${response.data.data.contract.numero}) no coincide con el del member (${this.member.NumeroContrato || this.member.ContratoId})`
              this.paymentIntentInfo = null
            }
          } else {
            this.paymentIntentError = response.data.error || 'Error consultando Payment Intent'
            this.paymentIntentInfo = null
          }
        } catch (error) {
          console.error('Error fetching Payment Intent info:', error)
          this.paymentIntentError = this.safeGet(error, 'response.data.error', 'Error de conexión')
          this.paymentIntentInfo = null
        } finally {
          this.loadingPaymentIntent = false
        }
      },
      
      async processManualPayment() {
        if (!this.canSubmit) return
        
        this.processingPayment = true
        this.paymentIntentError = ''
        this.mensualidadError = ''
        
        try {
                  const payload = {
          paymentIntentId: this.form.paymentIntentId,
          memberId: this.member._id,
          mensualidad: this.form.mensualidad,
          totalMensualidades: this.form.totalMensualidades // Puede ser null si se obtiene del PI
        }
          
          const response = await axios.post(`${API_URL}/financing/manual-payment`, payload)
          
                  if (response.data.success) {
          const captureType = this.hasValidStructure ? 'con estructura válida' : 'mediante captura manual'
          this.$buefy.toast.open({
            message: `Pago registrado exitosamente (${captureType}) - Mensualidad ${this.form.mensualidad}`,
            type: 'is-success',
            duration: 5000
          })
            
            this.$emit('payment-success', response.data.data)
            
            this.closeModal()
          } else {
            this.showError(response.data.error || 'Error al registrar el pago')
          }
        } catch (error) {
          console.error('Error processing manual payment:', error)
          const errorMessage = this.safeGet(error, 'response.data.error', 'Error de conexión al registrar el pago')
          this.showError(errorMessage)
        } finally {
          this.processingPayment = false
        }
      },
      
      showError(message) {
        this.$buefy.toast.open({
          message: message,
          type: 'is-danger',
          duration: 5000
        })
      }
    },
    
    beforeDestroy() {
      if (this.paymentIntentTimer) {
        clearTimeout(this.paymentIntentTimer)
      }
    }
  }
  </script>
  
  <style scoped>
  .modal-card {
    max-width: 650px;
    margin: 0 auto;
  }
  
  .modal-card-body .box {
    border-radius: 6px;
  }
  
  .box {
    box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1);
  }
  
  .subtitle.is-6 {
    line-height: 1.4;
  }
  
  .b-numberinput {
    width: 100%;
  }
  
  .tag {
    font-weight: 600;
  }
  
  .field:not(:last-child) {
    margin-bottom: 1.5rem;
  }
  
  .columns.is-vcentered {
    align-items: flex-end;
  }
  
  .modal-card-foot .button {
    margin-right: 0.75rem;
  }
  
  .modal-card-foot .button:last-child {
    margin-right: 0;
  }
  
  @media screen and (max-width: 768px) {
    .columns.is-vcentered .column.is-one-quarter {
      margin-top: 0;
    }
    
    .columns.is-vcentered .column.is-one-quarter .field {
      margin-top: 0.5rem;
    }
  }
  
  .box[style*="background-color: #fff8e1"] {
    border-left: 4px solid #ff9800;
  }
  
  .box[style*="background-color: #f0f8f0"] {
    border-left: 4px solid #4caf50;
  }
  
  .box[style*="background-color: #fafafa"] {
    border-left: 4px solid #2196f3;
  }
  </style>